import React, { useMemo, useState } from 'react';
import { AvTooltip, NoDataFound } from '@fabric-ui/components';
import { ActionButton, expandArrowColumn, FormatterType } from '@fabric-ui/components/Table';
import { Copy, Export } from '@fabric-ui/icons';
import NoDataFoundImg from '@fabric-ui/icons/svg/emptyStates/No Data Found.svg';
import { flex } from '@fabric-ui/theme';
import { Box, Button, capitalize, Collapse, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { exportToCSV } from '@utils/exportCSV.utils';
import { useSnackbar } from 'notistack';
import ReactDiffViewer, { DiffMethod, ReactDiffViewerStylesOverride } from 'react-diff-viewer';
import AvTable from '../../components/Table/AvTable';
import { useAvContext } from '../../context/AvContextProvider';
import { useGetUsersInfo } from '../../hooks/useGetUsersInfo';
import { buildPagination, useAuditLogs } from './hooks';
import { AuditEntityType, AuditEntry } from './types';

const copyIcon = <Copy style={{ width: 20, height: 20 }} />;

interface AuditLogsProps {
  id?: string;
  entityType: AuditEntityType;
  title: React.ReactNode;
  isQueryLoading?: boolean;
  defaultData?: AuditEntry[];
  codeMode?: boolean;
  pageSize?: number;
}

const stringifyForDiff = (data: object) =>
  JSON.stringify(data || {}, null, 2).replace(/^(\s*).*\\n.*$/gm, (line, indent) => line.replace(/\\n/g, `\n${indent}`));

const AuditLogs: React.FC<AuditLogsProps> = ({ id, entityType, title, isQueryLoading, defaultData, codeMode = false, pageSize }) => {
  const { isLoading } = useAvContext();
  const { palette } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [tablePage, setTablePage] = useState(0);
  const pageVars = useMemo(() => buildPagination({ page: tablePage, pageSize }), [tablePage]);
  const onError = error => enqueueSnackbar(error.message, { variant: 'error' });

  const {
    data: { rowsFromQuery = [], totalElements } = { rowsFromQuery: [], totalElements: 0 },
    isLoading: isLogsLoading,
    isError: logsError,
  } = useAuditLogs({ id, entityType, pageVars, onError });
  const rows = defaultData || rowsFromQuery;
  const { usersInfo: auditUsers } = useGetUsersInfo(rows.map(({ actorId }) => actorId));
  const rowsWithActorName = rows.map(row => ({ ...row, actorName: auditUsers[row.actorId!] }));
  const loading = isLoading || isLogsLoading || isQueryLoading;
  const shouldPresentRuleNameCol = [AuditEntityType.AUTOMATIONS, AuditEntityType.GROUPING_RULE_SET].includes(entityType);
  const diffViewerStyle: ReactDiffViewerStylesOverride = {
    variables: {
      light: {
        codeFoldGutterBackground: palette.colors.neutrals[300] as string,
        codeFoldBackground: palette.colors.neutrals[200] as string,
        diffViewerBackground: palette.colors.neutrals[200] as string,
        codeFoldContentColor: palette.colors.neutrals[900] as string,
        removedBackground: palette.colors.neutrals[200] as string,
        addedBackground: palette.colors.neutrals[200] as string,
      },
    },
    wordDiff: {
      borderRadius: '8px',
      paddingLeft: '8px',
      textIndent: '-6px',
    },
    diffAdded: {
      background: palette.colors.positive[100] as string,
      color: palette.colors.positive[600] as string,
    },
    wordAdded: {
      background: palette.colors.positive[100] as string,
      color: palette.colors.positive[600] as string,
    },
    wordRemoved: {
      background: palette.colors.negative[100] as string,
      color: palette.colors.negative[600] as string,
    },
    diffRemoved: {
      background: palette.colors.negative[100] as string,
      color: palette.colors.negative[600] as string,
    },
  };

  const codeFoldMessage = (totalFoldedLines: number) => (
    <Box
      sx={{
        '&.MuiTypography-root:hover': {
          color: theme => theme.palette.colors.primary[500],
        },
      }}>
      Expend {totalFoldedLines} Rows...
    </Box>
  );
  const expandedRow = (row: AuditEntry, onChange, isItemSelected) => (
    <Collapse in={isItemSelected} timeout="auto" unmountOnExit>
      <Box sx={{ padding: 2 }}>
        <ReactDiffViewer
          oldValue={stringifyForDiff(row.oldRowData)}
          newValue={stringifyForDiff(row.newRowData)}
          splitView={codeMode}
          hideLineNumbers={!codeMode}
          showDiffOnly={!codeMode}
          compareMethod={DiffMethod.LINES}
          extraLinesSurroundingDiff={0}
          styles={diffViewerStyle}
          codeFoldMessageRenderer={codeFoldMessage}
        />
      </Box>
    </Collapse>
  );

  const copyOldDataInlineAction = ({ oldRowData }) =>
    ActionButton(
      'Copy Previous Version',
      () => {
        navigator.clipboard.writeText(JSON.stringify(oldRowData, null, 2));
        enqueueSnackbar(`Previous Version Copied To Clipboard`, { variant: 'success' });
      },
      copyIcon
    );

  const headCells = shouldPresentRuleNameCol
    ? [...headCellsDefaults, { id: 'ruleName', label: 'Rule Name', getValue: row => row.newRowData.name || '' }]
    : headCellsDefaults;
  const exportTooltipText =
    rowsWithActorName.length > 0
      ? 'Download all visible audit logs, limited to the past 90 days'
      : 'No audit logs available from the past 90 days';
  return (
    <Box sx={{ backgroundColor: 'var(--color-background-primary)', ...flex.col, gap: 3 }}>
      <Box sx={{ ...flex.justifyBetweenCenter, h3: { margin: 0 } }}>
        <Box sx={{ ...flex.itemsCenter, gap: 2 }}>
          {title && (
            <Typography variant="h3" sx={{ ...flex.itemsCenter, gap: 2, whiteSpace: 'pre', margin: 0 }}>
              {title}
            </Typography>
          )}
        </Box>
        <AvTooltip title={exportTooltipText}>
          <Button
            sx={{ p: 1 }}
            variant="filter"
            className="transparent"
            onClick={
              rowsWithActorName.length > 0 ? () => exportToCSV(rows, { propertiesToStringify: ['oldRowData', 'newRowData'] }) : undefined
            }>
            <Export />
          </Button>
        </AvTooltip>
      </Box>
      {rowsWithActorName.length === 0 && !loading ? (
        <NoDataFound img={NoDataFoundImg} headline="No audit logs available from the past 90 days" />
      ) : (
        <AvTable
          resizable
          rows={rowsWithActorName}
          loading={!logsError && loading}
          pagination
          paginationProps={
            defaultData ? undefined : { onPageChange: page => setTablePage(+page), totalRows: totalElements, pageNum: tablePage }
          }
          headCells={headCells}
          enableSelectionColor={false}
          expandedRowObj={{ component: expandedRow }}
          allowEdit={false}
          allowKeyboardNavigation
          inlineActions={[copyOldDataInlineAction]}
          sx={{
            '.expanded-row, .expanded-row-content, .expanded-row-content .MuiTableCell-head': {
              backgroundColor: theme => theme.palette.colors.neutrals[200],
            },
            '.expanded-row-content > .MuiTableCell-root > div': {
              borderTop: '1px solid var(--color-border-base-secondary)',
            },
          }}
        />
      )}
    </Box>
  );
};

export default AuditLogs;

const headCellsDefaults = [
  {
    id: 'entryId',
    label: 'entryId',
    isKey: true,
    hidden: true,
  },
  expandArrowColumn(() => true),
  {
    id: 'dmlTimestamp',
    label: 'Time',
    type: FormatterType.date,
  },
  {
    id: 'operation',
    label: 'Operation',
    formatter: value => capitalize(value.toLowerCase()),
  },
  {
    id: 'type',
    label: 'Type',
  },
  {
    id: 'actorName',
    label: 'User Name',
  },
];
